import type {StreamData} from "@repo/guide-types";

/**
 * Process a streamed response of JSON objects.
 *
 * This processes a streamed response when the response is expected
 * to be a sequence of valid JSON objects. Whenever a complete JSON
 * oject is received in the response, the `onData` callback is invoked with
 * that response.
 *
 * Returns once the streamed response is finished.
 *
 * @param reader The reader to read the streamed response from.
 * @param onData The callback to invoke when a complete JSON object is
 * received.
 * @param onResponseStart The callback to invoke when the first chunk of
 * the response is received.
 * @returns A promise of the complete raw text received from the streamed
 * response.
 */
export const processStreamedResponse = async (
    // @ts-expect-error - TS2314 - Generic type 'ReadableStreamReader' requires 1 type argument(s).
    reader: ReadableStreamReader,
    onData: (jsonObject: StreamData) => void,
    onResponseStart?: null | (() => void),
): Promise<void> => {
    // The response comes back as a JSON lines text file in which every
    // new line (separated by a new line \n) contains a valid JSON
    // object. As part of each streamed chunk, we may receive multiple
    // complete JSON objects or none.
    let unprocessedResponse = "";
    let responseStarted = false;
    const decoder = new TextDecoder();

    // We loop through the reader, processing the information.
    // eslint-disable-next-line no-constant-condition -- porting over from old code
    while (true) {
        const {done, value} = await reader.read();

        if (!responseStarted) {
            responseStarted = true;
            // This is the first chunk of the response, so let's invoke
            // the onResponseStart callback.
            onResponseStart?.();
        }

        if (done || !value) {
            break;
        }

        // There is a complete JSON object before every new line. If we
        // split on new lines, then we can assert that for every chunk
        // before the last, there will be a complete JSON object.
        const decodedValue = decoder.decode(value);

        // Build the unprocessed response that we will process below.
        unprocessedResponse += decodedValue;

        const linesToProcess = unprocessedResponse.split("\n");
        for (const line of linesToProcess) {
            if (!line) {
                continue;
            }
            try {
                const jsonObject: StreamData = JSON.parse(line);
                onData(jsonObject);

                // We clearly processed part of the unprocessed response, so
                // we clear the unprocessed response knowing that any
                // unprocessed overflow will hit the catch below and repopulate
                // the unprocessed response.
                // Clearing it ensures that the valid JSON we processed is not
                // going to get processed again.
                unprocessedResponse = "";
            } catch (err: any) {
                if (err.name !== "SyntaxError") {
                    // It's not a parsing error, we should throw it on to
                    // our callers.
                    throw err;
                }

                // There will always be a valid JSON object before
                // every \n character. If the line isn't valid
                // JSON, it is because that this is the last chunk
                // in the split. We will re-assign jsonLines to include
                // this chunk in the next read.
                unprocessedResponse = line;
            }
        }
    }
};
